/**
 * Search over the settings sidebar.
 *
 * Ported from t3code's `settingsSearch`, but not its data. Theirs is a
 * hand-maintained catalog of ~200 individual settings with anchor ids, which
 * only stays correct if every panel remembers to register through it. Forge's
 * nav items already carry a label and a non-empty description — a fact its own
 * test asserts — so section-level search needs no second source of truth and
 * cannot drift from the nav it filters.
 *
 * What that trades away is finding an individual toggle by name. That is a
 * real gap and it is recorded as a follow-up rather than half-built: a search
 * that finds some settings and silently misses others is worse than one whose
 * scope a user can predict.
 */

/** The subset of a nav item this module needs — anything shaped like this works. */
export interface SettingsNavSearchItem {
  readonly label: string;
  readonly description: string;
}

export interface SettingsNavSearchGroup<TItem extends SettingsNavSearchItem> {
  readonly label: string;
  readonly items: ReadonlyArray<TItem>;
}

/**
 * Why an item matched, in the order results should rank.
 *
 * Ranking exists because descriptions are searched too. Without it, typing
 * "git" could put a section whose *description* happens to mention git above
 * the section actually called Git, which reads as a broken search even though
 * every result is technically a match.
 */
export type SettingsNavMatchKind = "label-prefix" | "label" | "description";

const MATCH_RANK: Readonly<Record<SettingsNavMatchKind, number>> = {
  "label-prefix": 0,
  label: 1,
  description: 2,
};

/** How an item matches a query, or null when it does not. */
export function matchSettingsNavItem(
  item: SettingsNavSearchItem,
  query: string,
): SettingsNavMatchKind | null {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return null;

  const label = item.label.toLowerCase();
  if (label.startsWith(q)) return "label-prefix";
  if (label.includes(q)) return "label";
  if (item.description.toLowerCase().includes(q)) return "description";
  return null;
}

/**
 * The groups to render for a query, with non-matching items and then empty
 * groups removed.
 *
 * Takes groups that are **already** platform- and flag-filtered. Searching a
 * pre-filtered list is what stops a query from surfacing a section the viewer
 * is not allowed to see — typing "sync" must not reveal CMMD Sync to someone
 * whose flag is off. Ordering the arguments any other way makes that an easy
 * mistake to make later, so this function deliberately cannot see the
 * unfiltered set.
 *
 * An empty query returns the groups untouched, including their original order.
 */
export function searchSettingsNavGroups<TItem extends SettingsNavSearchItem>(
  groups: ReadonlyArray<SettingsNavSearchGroup<TItem>>,
  query: string,
): ReadonlyArray<SettingsNavSearchGroup<TItem>> {
  if (query.trim().length === 0) return groups;

  return groups
    .map((group) => {
      const ranked = group.items
        .map((item) => ({ item, kind: matchSettingsNavItem(item, query) }))
        .filter(
          (entry): entry is { item: TItem; kind: SettingsNavMatchKind } => entry.kind !== null,
        )
        .toSorted((left, right) => MATCH_RANK[left.kind] - MATCH_RANK[right.kind]);

      return { ...group, items: ranked.map((entry) => entry.item) };
    })
    .filter((group) => group.items.length > 0);
}

/** How many sections a query matches, for the empty state and for tests. */
export function countSettingsNavMatches<TItem extends SettingsNavSearchItem>(
  groups: ReadonlyArray<SettingsNavSearchGroup<TItem>>,
  query: string,
): number {
  return searchSettingsNavGroups(groups, query).reduce(
    (total, group) => total + group.items.length,
    0,
  );
}
